import React, { useState, useRef, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';

import { useMyTasks } from '@/src/components/myTasks2/components/Tabs/MyTasks/api/getMyTasks';
import { MyPortfolioColumnsEnum } from '@/src/components/myTasks2/components/Tabs/MyPortfolio/types';

import WorkspaceTabs from './WorkspaceTabs';
import WorkspaceView from './WorkspaceView';
import './WorkspaceContainer.scss';
import useMyTasksSubscriptions from '../myTasks2/useMyTasksSubscriptions';
import { useSelector } from 'react-redux';
import { RootState } from '@/src/reducers/types';

import { Task } from '@/src/components/myTasks2/components/Tabs/MyPortfolio/types/task';
import SectionLoader from '../common/Elements/Spinner/SectionLoader/SectionLoader';

export type WorkspaceTab = 'all' | 'todos' | 'review';

interface WorkspaceContainerProps {
  isReportsEnabled?: boolean;
}

const WorkspaceContainer: React.FC<WorkspaceContainerProps> = ({ isReportsEnabled }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [activeTab, setActiveTab] = useState<WorkspaceTab>('all');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositions = useRef<{ [key in WorkspaceTab]?: number }>({});
  const loggedUser = useSelector((store: RootState) => store.loggedUser);

  const todosFilter = [
    {
      field: MyPortfolioColumnsEnum.TaskStatus,
      conditions: [
        {
          op: 'ne',
          val: 'Completed',
        },
      ],
    },
    { field: MyPortfolioColumnsEnum.ProjectId, conditions: [{ op: 'in', val: [projectId] }] },
    {
      field: MyPortfolioColumnsEnum.AssigneeId,
      conditions: [{ op: 'in', val: [loggedUser.data.id] }],
    },
  ];

  const reviewFilter = [
    {
      field: MyPortfolioColumnsEnum.TaskStatus,
      conditions: [{ op: 'in', val: ['To Review', 'To Approve'] }],
    },
    { field: MyPortfolioColumnsEnum.ProjectId, conditions: [{ op: 'in', val: [projectId] }] },
  ];

  const allStatuses = ['Delayed', 'To Do', 'Change Request', 'Approved', 'To Approve', 'To Review'];
  const allFilter = [
    {
      field: MyPortfolioColumnsEnum.TaskStatus,
      conditions: [{ op: 'in', val: allStatuses }],
    },
    { field: MyPortfolioColumnsEnum.ProjectId, conditions: [{ op: 'in', val: [projectId] }] },
    {
      field: MyPortfolioColumnsEnum.AssigneeId,
      conditions: [{ op: 'in', val: [loggedUser.data.id] }],
    },
  ];

  // Type for query keys
  type QueryKeyType = (string | object[])[];
  const allQueryKey: QueryKeyType = ['tasksList', 'MyTasks', allFilter];
  const todosQueryKey: QueryKeyType = ['tasksList', 'MyTasks', todosFilter];
  const reviewQueryKey: QueryKeyType = ['tasksList', 'MyTasks', reviewFilter];

  // Pick the correct queryKey for the active tab
  let currentQueryKey: QueryKeyType = allQueryKey;
  if (activeTab === 'all') {
    currentQueryKey = allQueryKey;
  } else if (activeTab === 'todos') {
    currentQueryKey = todosQueryKey;
  } else if (activeTab === 'review') {
    currentQueryKey = reviewQueryKey;
  }

  useMyTasksSubscriptions({
    queryKey: currentQueryKey,
    origin: 'myTasksDashboard',
  });

  const {
    data: allTasks,
    count: allTasksCount,
    queryInfo: allQueryInfo,
  } = useMyTasks({ filter: allFilter });

  const {
    data: todosTasksRaw,
    count: todosCount,
    queryInfo: todosQueryInfo,
  } = useMyTasks({ filter: todosFilter });

  const todosTasks = (todosTasksRaw || [])
    .filter((task: Task) => {
      if (['To Review', 'To Approve'].includes(task.taskStatus)) {
        return task.assigneeId === loggedUser.data.id;
      }
      return true;
    })
    .filter((task: Task) => {
      return (
        typeof task.taskId === 'number' &&
        task.taskId > 0 &&
        typeof task.taskStatus === 'string' &&
        task.taskStatus.length > 0
      );
    })
    .filter((task, idx, arr) => arr.findIndex((t) => t.taskId === task.taskId) === idx);

  const { data: reviewTasksRaw, queryInfo: reviewQueryInfo } = useMyTasks({ filter: reviewFilter });

  const filteredReviewTasks = (reviewTasksRaw || [])
    .filter((task: Task) => {
      if (task.taskStatus === 'To Review') {
        return (
          Number(task.reviewerId) === Number(loggedUser.data.id) &&
          Number(task.projectId) === Number(projectId)
        );
      }
      if (task.taskStatus === 'To Approve') {
        return (
          Number(task.approverId) === Number(loggedUser.data.id) &&
          Number(task.projectId) === Number(projectId)
        );
      }
      return false;
    })
    .filter((task: Task) => {
      return (
        typeof task.taskId === 'number' &&
        task.taskId > 0 &&
        typeof task.taskStatus === 'string' &&
        task.taskStatus.length > 0
      );
    })
    .filter((task, idx, arr) => arr.findIndex((t) => t.taskId === task.taskId) === idx);

  const reviewCount = filteredReviewTasks.length;

  // Save scroll position before tab change
  const handleTabChange = useCallback(
    (tab: WorkspaceTab) => {
      if (scrollContainerRef.current) {
        scrollPositions.current[activeTab] = scrollContainerRef.current.scrollTop;
      }
      setActiveTab(tab);
    },
    [activeTab]
  );

  // Restore scroll position after tab content mounts
  useEffect(() => {
    if (scrollContainerRef.current) {
      const pos = scrollPositions.current[activeTab] ?? 0;
      scrollContainerRef.current.scrollTop = pos;
    }
  }, [activeTab]);

  const allTasksFiltered: Task[] = ((allTasks || []) as Task[])
    .filter((task) => {
      const isTodos =
        !['To Review', 'To Approve'].includes(task.taskStatus) ||
        (['To Review', 'To Approve'].includes(task.taskStatus) &&
          task.assigneeId === loggedUser.data.id);
      const isReview =
        (task.taskStatus === 'To Review' &&
          task.reviewerId === loggedUser.data.id &&
          task.projectId == Number(projectId)) ||
        (task.taskStatus === 'To Approve' &&
          task.approverId === loggedUser.data.id &&
          task.projectId == Number(projectId));
      return isTodos || isReview;
    })
    .filter((task: Task) => {
      // Fallback: only allow tasks with valid taskId and known status
      return (
        typeof task.taskId === 'number' &&
        task.taskId > 0 &&
        typeof task.taskStatus === 'string' &&
        task.taskStatus.length > 0
      );
    });

  // For All tab, combine allFilter-matching tasks and filteredReviewTasks, deduped by taskId
  const allTasksFromFilter: Task[] = ((allTasks || []) as Task[])
    .filter((task) => {
      const isTodos =
        !['To Review', 'To Approve'].includes(task.taskStatus) ||
        (['To Review', 'To Approve'].includes(task.taskStatus) &&
          task.assigneeId === loggedUser.data.id);
      const isReview =
        (task.taskStatus === 'To Review' &&
          task.reviewerId === loggedUser.data.id &&
          task.projectId == Number(projectId)) ||
        (task.taskStatus === 'To Approve' &&
          task.approverId === loggedUser.data.id &&
          task.projectId == Number(projectId));
      return isTodos || isReview;
    })
    .filter((task: Task) => {
      return (
        typeof task.taskId === 'number' &&
        task.taskId > 0 &&
        typeof task.taskStatus === 'string' &&
        task.taskStatus.length > 0
      );
    });

  // Remove tasks with status 'Completed' from All tab (immediate UI update after optimistic update)
  const allTasksDeduped: Task[] = [...allTasksFromFilter, ...filteredReviewTasks]
    .filter((task, idx, arr) => arr.findIndex((t) => t.taskId === task.taskId) === idx)
    .filter((task) => task.taskStatus !== 'Completed');

  // Use filtered/deduped results for all, filtered for others
  let tasks = allTasksDeduped;
  let queryInfo = allQueryInfo;

  if (activeTab === 'todos') {
    tasks = todosTasks;
    queryInfo = todosQueryInfo;
  } else if (activeTab === 'review') {
    tasks = filteredReviewTasks;
    queryInfo = reviewQueryInfo;
  }

  const isLoading =
    allQueryInfo?.isLoading || todosQueryInfo?.isLoading || reviewQueryInfo?.isLoading;

  if (isLoading) {
    return (
      <SectionLoader
        className={clsx(
          'container--reports',
          'tw-h-[calc(100%-5px)] tw-w-[60%]',
          isReportsEnabled ? 'tw-max-h-[120px] tw-min-w-[100%] ' : 'tw-max-h-[inherit] tw-ml-[20px]'
        )}
      />
    );
  }
  return (
    <div
      className={clsx(
        'tw-bg-white',
        'tw-rounded-[25px]',
        'tw-shadow',
        'tw-p-8',
        'tw-mt-8',
        'tw-w-full',
        isReportsEnabled ? 'tw-max-w-[100%]' : 'tw-max-w-[48.5vw]',
        isReportsEnabled ? 'tw-ml-0' : 'tw-ml-[2rem]',
        isReportsEnabled ? 'tw-min-w-[100%]' : 'tw-min-w-[800px]'
      )}
    >
      <h4
        className={clsx(
          'header--title tw-text-[18px] tw-text-cfo-blue-600 tw-mr-[5px] tw-pb-[24px]'
        )}
      >
        My Workspace
      </h4>
      <WorkspaceTabs
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        isReportsEnabled={isReportsEnabled}
        tabCounts={{ all: allTasksCount + reviewCount, todos: todosCount, review: reviewCount }}
      />
      <div
        ref={scrollContainerRef}
        className={clsx(
          'tw-flex',
          'tw-flex-col',
          'tw-rounded-lg',
          'tw-overflow-x-auto',
          'tw-overflow-y-auto'
        )}
        style={{ maxHeight: '50vh', minHeight: '120px' }}
      >
        <WorkspaceView
          activeTab={activeTab}
          isReportsEnabled={isReportsEnabled}
          tasks={tasks || []}
          queryInfo={queryInfo}
        />
      </div>
    </div>
  );
};

export default WorkspaceContainer;
